import * as React from 'react';
import { useCallback, useState } from 'react';
import { Map } from 'immutable';
import { map, partial } from 'lodash';

import Box from '../../../components/Box';
import Button from '../../../components/clickables/Button';
import CheckboxInput from '../../../components/forms/CheckboxInput';
import FileInput from '../../../components/forms/FileInput';
import Label from '../../../components/forms/Label';
import TextField from '../../../components/forms/TextField';
import withRouterQueryParams from '../../../components/hocs/withRouterQueryParams';
import { Mobile } from '../../../components/MediaQuery';
import Text from '../../../components/Text';
import {
  applicantEmailIsValid,
  applicantPhoneIsValid,
  applicantZipIsValid,
} from '../../../features/hiring/util';
import { cxFunction } from '../../../util/className';
import { createKeyPressHandler } from '../../../util/keybindings';
import { parseQueryString, queryStringPresent } from '../../../util/router';
import {
  ELEMENTS,
  EVENT_ACTIONS,
  TRACK_ACTION_TYPES,
} from '../../../util/tracking_constants';
import { withUxTracking } from '../../../util/uxEvents';

import ApplicationQuestions from './ApplicationQuestions';

interface OwnProps {
  onSubmit: (data: Record<string, unknown>) => void;
  isSubmitting: boolean;
  existingUser: Map<string, unknown>;
  locationProfileUrl: string;
  locationCountryCode: string;
  hbUser?: string;
  jobRequest?: Map<string, unknown>;
  alertsExperimentEnabled?: boolean;
  trackUx?: (
    action: string,
    type: string,
    data: Record<string, unknown>
  ) => void;
}

interface CxProps {
  cx: (baseClass: string | null, ...modifiers: string[]) => string;
  cxEl: (
    element: string,
    ...modifiers: (string | { [key: string]: boolean })[]
  ) => string;
}

type ApplicationFormProps = OwnProps & CxProps;

interface ApplicationFormState {
  firstName: string;
  lastName: string;
  phone: string;
  zip: string;
  email: string;
  applicationAnswers: Record<string, string>;
  jobAlertsRole: boolean;
  jobAlertsLocation: boolean;
  resume: string | { dataUriScheme: string; file: string };
}

const ApplicationFormComponent = (
  props: ApplicationFormProps
): React.ReactElement => {
  const {
    onSubmit,
    isSubmitting,
    existingUser,
    locationProfileUrl,
    locationCountryCode,
    hbUser,
    jobRequest,
    alertsExperimentEnabled,
    trackUx,
    cx,
  } = props;

  const [formState, setFormState] = useState<ApplicationFormState>({
    firstName: (existingUser.get('first_name') as string) || '',
    lastName: (existingUser.get('last_name') as string) || '',
    phone: (existingUser.get('phone') as string) || '',
    zip: (existingUser.get('zip') as string) || '',
    email: (existingUser.get('email') as string) || '',
    applicationAnswers: {},
    jobAlertsRole: true,
    jobAlertsLocation: true,
    resume: '',
  });

  const handleJobAlertsRoleChange = useCallback(
    (value: boolean) => {
      if (trackUx) {
        trackUx(
          value
            ? EVENT_ACTIONS.CHECKBOX_SELECTED
            : EVENT_ACTIONS.CHECKBOX_UNSELECTED,
          TRACK_ACTION_TYPES.CLICK,
          {
            element: ELEMENTS.JOB_ALERTS_ROLE_OPTIN,
          }
        );
      }
      setFormState(prevState => ({ ...prevState, jobAlertsRole: value }));
    },
    [trackUx]
  );

  const handleJobAlertsLocationChange = useCallback(
    (value: boolean) => {
      if (trackUx) {
        trackUx(
          value
            ? EVENT_ACTIONS.CHECKBOX_SELECTED
            : EVENT_ACTIONS.CHECKBOX_UNSELECTED,
          TRACK_ACTION_TYPES.CLICK,
          {
            element: ELEMENTS.JOB_ALERTS_LOCATION_OPTIN,
          }
        );
      }
      setFormState(prevState => ({ ...prevState, jobAlertsLocation: value }));
    },
    [trackUx]
  );

  const handleInputChange = useCallback((name: string, value: string) => {
    setFormState(prevState => ({ ...prevState, [name]: value }));
  }, []);

  const handleFirstNameChange = partial(handleInputChange, 'firstName');
  const handleLastNameChange = partial(handleInputChange, 'lastName');
  const handlePhoneChange = partial(handleInputChange, 'phone');
  const handleZipChange = partial(handleInputChange, 'zip');
  const handleEmailChange = partial(handleInputChange, 'email');

  const zipMask = useCallback(
    () => (locationCountryCode === 'CA' ? 'canadianZip' : 'zip'),
    [locationCountryCode]
  );

  const handleAnswerChange = useCallback((key: string, value: string) => {
    setFormState(prevState => ({
      ...prevState,
      applicationAnswers: {
        ...prevState.applicationAnswers,
        [key]: value,
      },
    }));
  }, []);

  const applicationAnswersPayload = useCallback(() => {
    if (!jobRequest) return {};

    const questions = jobRequest.get('application_questions');
    if (!questions) return {};

    return map(formState.applicationAnswers, (value, key) => ({
      question_id: key,
      answer: value,
    }));
  }, [formState.applicationAnswers, jobRequest]);

  const handleResumeAdded = useCallback((encodedFile: string) => {
    const [dataUriScheme, file] = encodedFile.split(',');
    setFormState(prevState => ({
      ...prevState,
      resume: { dataUriScheme, file },
    }));
  }, []);

  const handleResumeRemoved = useCallback(() => {
    setFormState(prevState => ({
      ...prevState,
      resume: '',
    }));
  }, []);

  const submitIsDisabled = useCallback(
    () =>
      !(
        formState.firstName.length > 0 &&
        formState.lastName.length > 0 &&
        applicantPhoneIsValid(formState.phone) &&
        applicantZipIsValid(formState.zip, zipMask()) &&
        applicantEmailIsValid(formState.email)
      ),
    [formState, zipMask]
  );

  const handleSubmit = useCallback(() => {
    if (submitIsDisabled()) return;

    const initialPayload = {
      form: {
        first_name: formState.firstName,
        last_name: formState.lastName,
        phone: formState.phone,
        zip: formState.zip,
        email: formState.email,
        application_answers: applicationAnswersPayload(),
        resume: formState.resume,
        job_alerts_role: formState.jobAlertsRole,
        job_alerts_location: formState.jobAlertsLocation,
      },
      location_profile_url: locationProfileUrl,
    };

    if (hbUser) {
      (initialPayload.form as Record<string, unknown>).hb_user = hbUser;
    }

    if (queryStringPresent('original_source')) {
      (initialPayload.form as Record<string, unknown>).original_source =
        parseQueryString().original_source;
    }

    if (queryStringPresent('source')) {
      (initialPayload.form as Record<string, unknown>).source =
        parseQueryString().source;
    }

    return onSubmit(initialPayload);
  }, [
    formState,
    submitIsDisabled,
    applicationAnswersPayload,
    locationProfileUrl,
    hbUser,
    onSubmit,
  ]);

  const handleKeyDown = createKeyPressHandler({
    Enter: handleSubmit,
  });

  return (
    <Mobile>
      {(mobile: boolean) => (
        <Box className={cx('ApplicationForm')} onKeyDown={handleKeyDown}>
          <Box row={!mobile} mvm>
            <Box grow basis={200} mrm={!mobile}>
              <Label>
                <Text
                  blackLight
                  fw400
                  fs16
                  i18n="hiring.application_form.first_name"
                />
              </Label>
              <Box mtxs bg="white">
                <TextField
                  theme="bordered"
                  name="first_name"
                  value={formState.firstName}
                  onChange={handleFirstNameChange}
                />
              </Box>
            </Box>

            <Box grow basis={200} mtm={mobile}>
              <Label>
                <Text
                  blackLight
                  fw400
                  fs16
                  i18n="hiring.application_form.last_name"
                />
              </Label>
              <Box mtxs bg="white">
                <TextField
                  theme="bordered"
                  name="last_name"
                  value={formState.lastName}
                  onChange={handleLastNameChange}
                />
              </Box>
            </Box>
          </Box>

          <Box row={!mobile} mvm>
            <Box grow basis={200} mrm={!mobile}>
              <Label>
                <Text
                  blackLight
                  fw400
                  fs16
                  i18n="hiring.application_form.phone"
                />
              </Label>
              <Box mtxs bg="white">
                <TextField
                  theme="bordered"
                  name="phone"
                  mask="phone"
                  value={formState.phone}
                  onChange={handlePhoneChange}
                />
              </Box>
            </Box>

            <Box grow basis={200} mtm={mobile}>
              <Label>
                <Text
                  blackLight
                  fw400
                  fs16
                  i18n="hiring.application_form.zip"
                />
              </Label>
              <Box mtxs bg="white">
                <TextField
                  theme="bordered"
                  name="zip"
                  mask={zipMask()}
                  value={formState.zip}
                  onChange={handleZipChange}
                />
              </Box>
            </Box>
          </Box>

          <Box mvm>
            <Box grow basis={200}>
              <Label>
                <Text
                  blackLight
                  fw400
                  fs16
                  i18n="hiring.application_form.email"
                />
              </Label>
              <Box mtxs bg="white">
                <TextField
                  theme="bordered"
                  name="email"
                  value={formState.email}
                  onChange={handleEmailChange}
                />
              </Box>
            </Box>
          </Box>

          {!jobRequest?.get('is_v2') && (
            <ApplicationQuestions
              questions={
                jobRequest?.get('application_questions') as
                  | Map<string, unknown>[]
                  | undefined
              }
              answers={formState.applicationAnswers}
              onAnswerChange={handleAnswerChange}
            />
          )}

          {alertsExperimentEnabled && (
            <Box mvm>
              <Box mbs>
                <CheckboxInput
                  value={formState.jobAlertsRole}
                  label={
                    <Text
                      fs16
                      i18n="hiring.application_form.job_alerts.role"
                      i18nProps={{
                        role: jobRequest?.get('role_name') as string,
                        city: jobRequest?.get('location_city') as string,
                      }}
                    />
                  }
                  onChange={handleJobAlertsRoleChange}
                />
              </Box>

              <Box>
                <CheckboxInput
                  value={formState.jobAlertsLocation}
                  label={
                    <Text
                      fs16
                      i18n="hiring.application_form.job_alerts.location"
                      i18nProps={{
                        location: jobRequest?.get('location_name') as string,
                      }}
                    />
                  }
                  onChange={handleJobAlertsLocationChange}
                />
              </Box>
            </Box>
          )}

          <Box mvm>
            <Box grow basis={200}>
              <Box pts>
                <FileInput
                  name="resume"
                  onFileAdded={handleResumeAdded}
                  onFileRemoved={handleResumeRemoved}
                  accept=".doc,.docx,.pdf"
                  readDataAsUrl
                >
                  {({
                    handleClick,
                    handleRemoveFile,
                    fileName,
                  }: {
                    handleClick: () => void;
                    handleRemoveFile: () => void;
                    fileName: string;
                  }) => (
                    <Box row vcenter>
                      {!fileName && (
                        <Box column>
                          <a onClick={handleClick}>
                            <Text
                              fs16
                              blue
                              underline
                              fw400
                              i18n="hiring.application_form.resume"
                            />
                          </a>
                        </Box>
                      )}

                      {fileName && (
                        <Box row vcenter>
                          <Box column>
                            <Text fs16 fw600 blue italic>
                              {fileName}
                            </Text>
                          </Box>

                          <Box column mls>
                            <a onClick={handleRemoveFile}>
                              <Text fs14 fw700 color="grayXdark">
                                X
                              </Text>
                            </a>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </FileInput>
              </Box>
            </Box>
          </Box>

          <Button
            fullWidth
            size="medium"
            theme={submitIsDisabled() ? 'secondary-blue' : 'primary-blue'}
            i18n="hiring.application_form.submit_application"
            disabled={submitIsDisabled()}
            loading={isSubmitting}
            onClick={handleSubmit}
          />
        </Box>
      )}
    </Mobile>
  );
};

ApplicationFormComponent.defaultProps = {
  existingUser: Map(),
};

const ApplicationForm = withUxTracking()(
  withRouterQueryParams('hb_user')(
    cxFunction('ApplicationForm', ApplicationFormComponent)
  )
);

export default ApplicationForm;
